import { useState } from 'react'
import {
  Card, Table, Tag, Space, InputNumber, Typography, Statistic, Tooltip, Segmented, Button,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { ReloadOutlined } from '@ant-design/icons'
import { api } from '../services/api'
import type { AlertItem, AlertsResponse } from '../services/api'

const { Text } = Typography

const SOURCE_META: Record<string, { label: string; color: string }> = {
  order_failed: { label: '下单/平仓失败', color: 'red' },
  slow_webhook: { label: '慢响应', color: 'orange' },
  system_exception: { label: '系统异常', color: 'volcano' },
  manual_order_failed: { label: '主动下单失败', color: 'red' },
}

const SEVERITY_COLOR: Record<string, string> = {
  critical: '#cf1322',
  error: '#cf1322',
  warning: '#d46b08',
}

function fmtTime(v?: string | null) {
  if (!v) return '-'
  const d = dayjs(v)
  return d.isValid() ? d.format('MM-DD HH:mm:ss') : v
}

export default function Alerts() {
  const [hours, setHours] = useState(24)
  const [slowMs, setSlowMs] = useState(5000)
  const [category, setCategory] = useState<string>('all')

  const { data, isLoading, isFetching, refetch } = useQuery<AlertsResponse>({
    queryKey: ['alerts-recent', hours, slowMs],
    queryFn: () =>
      api.alerts.getRecent(hours, slowMs),
    refetchInterval: 30_000,
  })

  const items = (data?.items || []).filter(
    (i) => category === 'all' || i.source === category,
  )

  const columns: ColumnsType<AlertItem> = [
    {
      title: '时间',
      width: 150,
      render: (_, r) => <Text style={{ fontSize: 12 }}>{fmtTime(r.occurred_at)}</Text>,
      defaultSortOrder: 'descend',
      sorter: (a, b) => String(a.occurred_at).localeCompare(String(b.occurred_at)),
    },
    {
      title: '类别',
      width: 130,
      render: (_, r) => {
        const meta = SOURCE_META[r.source] || { label: r.source, color: 'default' }
        return <Tag color={meta.color}>{meta.label}</Tag>
      },
      filters: Object.entries(SOURCE_META).map(([k, v]) => ({ text: v.label, value: k })),
      onFilter: (v, r) => r.source === v,
    },
    { title: '标题', dataIndex: 'title', width: 190 },
    {
      title: 'Token',
      width: 140,
      render: (_, r) => r.token_name || (r.token_id != null ? `#${r.token_id}` : '-'),
    },
    {
      title: '品种/方向',
      width: 140,
      render: (_, r) =>
        r.symbol ? `${r.symbol}${r.side ? ` · ${r.side}` : ''}` : '-',
    },
    {
      title: '耗时',
      width: 90,
      render: (_, r) =>
        r.latency_ms != null ? (
          <Text type={r.latency_ms >= 10000 ? 'danger' : undefined}>
            {(r.latency_ms / 1000).toFixed(1)}s
          </Text>
        ) : (
          '-'
        ),
      sorter: (a, b) => (a.latency_ms ?? 0) - (b.latency_ms ?? 0),
    },
    {
      title: '详情',
      ellipsis: true,
      render: (_, r) => (
        <Tooltip title={r.detail} styles={{ root: { maxWidth: 520 } }}>
          <Text style={{ fontSize: 12 }} code>{String(r.detail || '-').slice(0, 120)}</Text>
        </Tooltip>
      ),
    },
    {
      title: '关联',
      width: 170,
      render: (_, r) => (
        <Space size={4} wrap>
          {r.signal_id ? <Text code style={{ fontSize: 11 }}>{String(r.signal_id).slice(0, 18)}</Text> : null}
          {r.resolved === false ? <Tag color="red">未解决</Tag> : null}
          {r.resolved === true ? <Tag color="green">已解决</Tag> : null}
        </Space>
      ),
    },
  ]

  const s = data?.summary
  const segOptions = [
    { label: `全部 ${s?.total ?? 0}`, value: 'all' },
    { label: `下单失败 ${s?.order_failed ?? 0}`, value: 'order_failed' },
    { label: `慢响应 ${s?.slow_webhook ?? 0}`, value: 'slow_webhook' },
    { label: `系统异常 ${s?.system_exception ?? 0}`, value: 'system_exception' },
    { label: `主动单失败 ${s?.manual_order_failed ?? 0}`, value: 'manual_order_failed' },
  ]

  return (
    <div style={{ padding: 16 }}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space size={24} wrap align="center">
          <Statistic title="告警总数" value={s?.total ?? 0} />
          <Statistic
            title="错误类"
            value={(s?.order_failed ?? 0) + (s?.manual_order_failed ?? 0) + (s?.system_exception ?? 0)}
            valueStyle={{ color: '#cf1322' }}
          />
          <Statistic
            title={`慢响应 ≥${((data?.window.slow_ms ?? slowMs) / 1000).toFixed(0)}s`}
            value={s?.slow_webhook ?? 0}
            valueStyle={{ color: '#d46b08' }}
          />
          <Space size={8} wrap>
            <Text type="secondary">窗口</Text>
            <InputNumber min={1} max={168} value={hours} onChange={(v) => setHours(Number(v) || 24)} />
            <Text type="secondary">小时</Text>
            <InputNumber
              min={1000}
              step={1000}
              value={slowMs}
              onChange={(v) => setSlowMs(Number(v) || 5000)}
            />
            <Text type="secondary">ms 阈值</Text>
          </Space>
          <Segmented options={segOptions} value={category} onChange={(v) => setCategory(v as string)} />
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>刷新</Button>
        </Space>
      </Card>

      <Table<AlertItem>
        rowKey={(r, idx) => `${r.source}-${r.occurred_at}-${idx}`}
        size="small"
        loading={isLoading}
        dataSource={items}
        columns={columns}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        scroll={{ x: 1100 }}
      />
    </div>
  )
}
